import React, {useEffect, useState} from 'react';
import {InlineAlert} from '@/components/alerts';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import {FeedbackEvent} from 'entities/event';
import {buildURL} from '@/utils/urlBuilder';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import Icon from '@/components/icons/Icon';

interface Props {
    eventData: FeedbackEvent | null | undefined;
}

// Extended interface to handle potential additional properties
interface ExtendedEventData extends FeedbackEvent {
    eventFeedbackId?: number;
    [key: string]: any; // Allow for other dynamic properties
}

const FeedbackDetailBlock = React.memo<Props>(({eventData}) => {
    const {config, loading: configLoading} = useOptimizedConfig();
    
    // Add null checks and use the correct property
    // Try multiple possible property names for the event ID
    const extendedEventData = eventData as ExtendedEventData;
    const eventId = extendedEventData?.event_id || extendedEventData?.id || extendedEventData?.eventFeedbackId;
    
    const feedbackUrl = eventId 
        ? buildURL('getFeedbackConversation', {event_id: eventId})
        : null;

    const [activeFeedbackData, loadingFeedback] = useData<any>(
        feedbackUrl, 'eventDetail', 'setActiveFeedbackData', 'activeFeedbackData',
    );
    
    // Avoid showing a "flash" of the loader for very fast responses
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        if (loadingFeedback) {
            timer = window.setTimeout(() => setShowLoader(true), 200);
        } else {
            setShowLoader(false);
        }

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loadingFeedback]);

    // Show loading while config is loading
    if (configLoading || !config) {
        return <CellLoader text={i18n.t('loading_configuration.text')} />;
    }
    
    // Early return if eventData is null or undefined
    if (!eventData) {
        return (
            <InlineAlert 
                variant="warning" 
                message={i18n.t('jt_feedback_not.text')}
                className="mb-3"
            />
        );
    }
    
    if (!eventId) {
        return (
            <InlineAlert 
                variant="warning" 
                message={i18n.t('jt_feedback_not.text')}
                className="mb-3"
            />
        );
    }

    let scrollDiv;

    // Normalize responses to a safe array
    const responses = Array.isArray(activeFeedbackData?.response)
        ? activeFeedbackData.response
        : [];

    if (loadingFeedback && showLoader) {
        scrollDiv = (
            <CellLoader fullHeight={true} />
        );
    } else if (!activeFeedbackData || responses.length === 0) {
        scrollDiv = (
            <InlineAlert 
                variant="warning" 
                message={i18n.t('jt_feedback_not.text')}
                className="mb-3"
            />
        );
    } else {
        scrollDiv = (
            responses.map((item: any, index: any) => (
                <div key={index} className='steamline mt-5'>
                    <div className='sl-item'>
                        <div className='sl-left bg-success'><Icon name='mdi-account' /></div>
                        <div className='sl-right'>
                            <div className='font-medium'>{item.question}</div>
                            <div className='desc overflow-wrap-anywhere'>{item.answer}</div>
                        </div>
                    </div>

                </div>
            ))
        );
    }

    return (
        <div>
            {scrollDiv}
        </div>
    );
});


export default FeedbackDetailBlock;
